// region import React
import React from 'react'
import * as d3 from 'd3'
import './styles.scss'

class FeatureGraph extends React.Component {
  // -- D3
  static windowWidth = window.innerWidth
  static isMobile = FeatureGraph.windowWidth < 720
  static svgWidth = 280
  static svgHeight = 240
  static margin = { top: 30, right: 10, bottom: 70, left: 10 }
  static groupWidth =
    FeatureGraph.svgWidth - FeatureGraph.margin.left - FeatureGraph.margin.right
  static groupHeight =
    FeatureGraph.svgHeight -
    FeatureGraph.margin.top -
    FeatureGraph.margin.bottom

  // TODO dynamic xDomain based on age
  static xDomainLower = 16
  static xDomainUpper = 72
  static generations = [
    'Generation Z', // Gen Z, 1994-present, Age below 24, l: 16, m: 19.5, u: 23
    'Generation Y', // Gen Y, 1981-1994, Age 24-37, l: 24, m: 30.5, u: 37
    'Generation X', // Gen X, 1965-1980, Age 38-53, l: 38, m: 45.5, u: 53
    'Babyboomer' // Babyboomer, 1946-1964, Age 54-72, l: 54, m: 63, u: 72
  ]
  static generationsAges = [
    '(<25 J.)',
    '(25–38 J.)',
    '(39–54 J.)',
    '(55–73 J.)'
  ]

  // Ticks
  static setTick(value, xScale) {
    var factor =
      (value - xScale.domain()[0]) / (xScale.domain()[1] - xScale.domain()[0])
    return FeatureGraph.groupWidth * factor
  }

  // Scales
  static xScale = d3
    .scaleLinear()
    .domain([FeatureGraph.xDomainLower, FeatureGraph.xDomainUpper])
    .range([0, FeatureGraph.groupWidth])
  static xScaleOrdinal = d3
    .scaleOrdinal()
    .domain(FeatureGraph.generations)
    .range([
      FeatureGraph.setTick(19.5, FeatureGraph.xScale),
      FeatureGraph.setTick(30.5, FeatureGraph.xScale),
      FeatureGraph.setTick(45.5, FeatureGraph.xScale),
      FeatureGraph.setTick(63, FeatureGraph.xScale)
    ])
  static xScaleOrdinalAges = d3
    .scaleOrdinal()
    .domain(FeatureGraph.generationsAges)
    .range([
      FeatureGraph.setTick(19.5, FeatureGraph.xScale),
      FeatureGraph.setTick(30.5, FeatureGraph.xScale),
      FeatureGraph.setTick(45.5, FeatureGraph.xScale),
      FeatureGraph.setTick(63, FeatureGraph.xScale)
    ])

  // Line
  static line = d3
    .line()
    .x(function(d, i) {
      return FeatureGraph.xScaleOrdinal(i)
    })
    .y(function(d) {
      return FeatureGraph.yScale(d.value)
    })

  // Circles
  static renderCircles = function(dataset) {
    return dataset.map((d, i) => (
      <circle
        className='circle'
        cx={FeatureGraph.xScaleOrdinal(i)}
        cy={FeatureGraph.yScale(d.value)}
        r={4}
        style={{ fill: '#ffffff' }}
        key={Math.random() * 1}
      />
    ))
  }

  // Popup
  static populatePopup = function(elem, elemClass, id, feature) {
    if (FeatureGraph.isMobile) {
      d3.selectAll('.tooltip')
        .html('')
        .style('display', 'none')
      let tooltip = d3.select('.feature-' + feature + ' .tooltip').html('')
      if (!elem.classed('active')) {
        d3.selectAll('.play-button').classed('active', false)
        tooltip
          .style('display', 'inline-block')
          .attr('class', 'tooltip ' + elemClass)
          .html(
            '<iframe src="https://open.spotify.com/embed/track/' +
              id +
              '" width="300" height="80" frameborder="0" allowtransparency="true" allow="encrypted-media"></iframe>'
          )
        elem.classed('active', true)
      } else {
        d3.selectAll('.play-button').classed('active', false)
        tooltip.style('display', 'none').attr('class', 'tooltip')
      }
    }
  }

  static returnAxis = function(generationValues, userValue, avgValue) {
    let arr = Object.keys(generationValues).map(function(key) {
      return generationValues[key].value
    })
    if (userValue && avgValue) {
      arr.push(userValue, avgValue)
    }
    let min = Math.min.apply(null, arr)
    let max = Math.max.apply(null, arr)
    let yDomainLower = min - (max - min) / 2
    let yDdomainUpper = max + (max - min) / 2

    return d3
      .scaleLinear()
      .domain([yDomainLower < 0 ? 0 : yDomainLower, yDdomainUpper])
      .range([FeatureGraph.groupHeight, 0])
  }

  render() {
    const userValue = this.props.userValue
    const userAge = this.props.userAge
    const avgValue = this.props.avgValue
    const avgAge = this.props.avgAge

    FeatureGraph.yScale = FeatureGraph.returnAxis(
      this.props.data.values,
      userValue,
      avgValue,
      FeatureGraph.groupHeight
    )

    const topRight = {
      rotation: -45,
      pathX: 4,
      pathY: -9,
      textX: 30,
      textY: -14,
      textAnchor: 'start',
      scale: 'scale(1,1)'
    }
    const bottomRight = {
      rotation: 13,
      pathX: 9,
      pathY: 2,
      textX: 30,
      textY: 24,
      textAnchor: 'start',
      scale: 'scale(1,1)'
    }

    const topLeft = {
      rotation: 45,
      pathX: -5,
      pathY: -9,
      textX: -30,
      textY: -16,
      textAnchor: 'end',
      scale: 'scale(-1,1)'
    }
    const bottomLeft = {
      rotation: -13,
      pathX: -9,
      pathY: 2,
      textX: -32,
      textY: 20,
      textAnchor: 'end',
      scale: 'scale(-1,1)'
    }

    let avgTransformVars = {
      rotation: 0,
      pathX: 0,
      pathY: 0,
      textX: 0,
      textY: 0,
      textAnchor: 'start',
      scale: 'scale(1,1)'
    }
    let userTransformVars = {
      rotation: 0,
      pathX: 0,
      pathY: 0,
      textX: 0,
      textY: 0,
      textAnchor: 'start',
      scale: 'scale(1,1)'
    }

    if (userAge < 50 && userValue > avgValue) {
      avgTransformVars = bottomRight
      userTransformVars = topRight
    } else if (userAge < 50 && userValue < avgValue) {
      avgTransformVars = topRight
      userTransformVars = bottomRight
    } else if (userAge > 50 && userValue > avgValue) {
      avgTransformVars = topLeft
      userTransformVars = bottomLeft
    } else {
      avgTransformVars = bottomLeft
      userTransformVars = topLeft
    }

    const avgTransformX = FeatureGraph.xScale(avgAge)
    const avgTransformY = FeatureGraph.yScale(avgValue)

    const userTransformX = FeatureGraph.xScale(userAge)
    const userTransformY = FeatureGraph.yScale(userValue)

    return (
      <>
        <div
          className={`column sixteen wide mobile fourteen wide tablet twelve wide computer featuregraph feature-${this.props.data.key}`}
        >
          <h4 className='ui left aligned header feature'>
            {this.props.data.name}
          </h4>

          {this.props.data.info && (
            <div className='info-text'>
              <i className='icon info circle' />
              {this.props.data.info}
            </div>
          )}

          <div className='tooltip-wrapper'>
            <svg
              className='featuregraphSvg'
              width='100%'
              viewBox={`0 0 ${FeatureGraph.svgWidth} ${FeatureGraph.svgHeight}`}
            >
              {/* Background */}
              <rect height='100%' width='100%' fill='#353535' />

              {/* Main Group */}
              <g
                className='svgGroup'
                transform={`translate(${FeatureGraph.margin.left}, ${FeatureGraph.margin.top})`}
              >
                {/* Gridlines */}
                <g
                  className='grid'
                  ref={node =>
                    d3.select(node).call(
                      d3
                        .axisTop()
                        .tickFormat('')
                        .tickSize(-FeatureGraph.groupHeight)
                        .scale(FeatureGraph.xScaleOrdinal)
                    )
                  }
                />

                {/* Play-Axis */}
                <g className='playAxis' x={`${FeatureGraph.groupWidth}`} y='0'>
                  <line
                    x1={`${FeatureGraph.groupWidth}`}
                    x2={`${FeatureGraph.groupWidth}`}
                    y1={`${-12.5}`}
                    y2={`${FeatureGraph.groupHeight + 12.5}`}
                  />
                  <g
                    className='play-button'
                    data-id={this.props.data.songhigh}
                    data-feature={this.props.data.key}
                    ref={node =>
                      d3.select(node).on('click', function() {
                        FeatureGraph.populatePopup(
                          d3.select(this),
                          'tooltip-top',
                          d3.select(this).attr('data-id'),
                          d3.select(this).attr('data-feature')
                        )
                      })
                    }
                  >
                    <circle
                      cx={`${FeatureGraph.groupWidth}`}
                      cy={`${-12.5}`}
                      r='9'
                      fill='#585858'
                      style={{
                        display: FeatureGraph.isMobile ? 'block' : 'none'
                      }}
                    />
                    <text
                      className='text-top'
                      x={`${FeatureGraph.groupWidth - 12.5}`}
                      y={`${-8}`}
                      style={{
                        textAnchor: 'end',
                        fontSize: '10px',
                        fill: '#bebebe'
                      }}
                    >
                      {this.props.data.texthigh}
                    </text>
                    <path
                      transform={`translate(${FeatureGraph.groupWidth -
                        3}, -17)`}
                      d='M7.5 4.5l-7 4v-8z'
                      fill='#FFFFFF'
                      style={{
                        display: FeatureGraph.isMobile ? 'block' : 'none'
                      }}
                    />
                  </g>
                  <g
                    className='play-button'
                    data-id={this.props.data.songlow}
                    data-feature={this.props.data.key}
                    ref={node =>
                      d3.select(node).on('click', function() {
                        FeatureGraph.populatePopup(
                          d3.select(this),
                          'tooltip-bottom',
                          d3.select(this).attr('data-id'),
                          d3.select(this).attr('data-feature')
                        )
                      })
                    }
                  >
                    <circle
                      cx={`${FeatureGraph.groupWidth}`}
                      cy={`${FeatureGraph.groupHeight + 12}`}
                      r='9'
                      fill='#585858'
                      style={{
                        display: FeatureGraph.isMobile ? 'block' : 'none'
                      }}
                    />
                    <text
                      className='text-bottom'
                      x={`${FeatureGraph.groupWidth - 12.5}`}
                      y={`${FeatureGraph.groupHeight + 12.5 + 4.5}`}
                      style={{
                        textAnchor: 'end',
                        fontSize: '10px',
                        fill: '#bebebe'
                      }}
                    >
                      {this.props.data.textlow}
                    </text>
                    <path
                      transform={`translate(${FeatureGraph.groupWidth -
                        3}, ${FeatureGraph.groupHeight + 12.5 - 4.5})`}
                      d='M7.5 4.5l-7 4v-8z'
                      fill='#FFFFFF'
                      style={{
                        display: FeatureGraph.isMobile ? 'block' : 'none'
                      }}
                    />
                  </g>

                  <circle
                    cx={`${FeatureGraph.groupWidth}`}
                    cy={`${-12.5}`}
                    r='2'
                    fill='#ffa800'
                    style={{
                      display: FeatureGraph.isMobile ? 'none' : 'block'
                    }}
                  />
                  <circle
                    cx={`${FeatureGraph.groupWidth}`}
                    cy={`${FeatureGraph.groupHeight + 12.5}`}
                    r='2'
                    fill='#ffa800'
                    style={{
                      display: FeatureGraph.isMobile ? 'none' : 'block'
                    }}
                  />
                </g>

                {/* Generation-Axis */}
                <g
                  className='axis axis-gen'
                  transform={`translate(0, ${FeatureGraph.groupHeight + 25})`}
                  ref={node =>
                    d3
                      .select(node)
                      .call(
                        d3
                          .axisBottom()
                          .tickValues(FeatureGraph.generations)
                          .scale(FeatureGraph.xScaleOrdinal)
                      )
                      .selectAll('text')
                      .each(function(d, i) {
                        let shift = [0, 10, 10, 10]
                        let tick = d3.select(this)
                        tick.attr('transform', 'translate(' + shift[i] + ', 0)')
                      })
                  }
                />
                <g
                  className='axis axis-gen'
                  transform={`translate(0, ${FeatureGraph.groupHeight + 25})`}
                  ref={node =>
                    d3
                      .select(node)
                      .call(
                        d3
                          .axisBottom()
                          .tickValues(FeatureGraph.generationsAges)
                          .scale(FeatureGraph.xScaleOrdinalAges)
                      )
                      .selectAll('text')
                      .each(function(d, i) {
                        let shift = [0, 10, 10, 10]
                        let tick = d3.select(this)
                        tick.attr(
                          'transform',
                          'translate(' + shift[i] + ', 12)'
                        )
                      })
                  }
                />

                {/* Line */}
                <path
                  className='line'
                  d={FeatureGraph.line(this.props.data.values)}
                />

                {/* Dots */}
                <g ref='dots'>
                  {FeatureGraph.renderCircles(this.props.data.values)}
                  {avgValue && avgAge ? (
                    <circle
                      className='circle-avg'
                      cx={FeatureGraph.xScale(avgAge)}
                      cy={FeatureGraph.yScale(avgValue)}
                      r={6}
                      ref={node => d3.select(node).attr('fill', '#007ABF')}
                    />
                  ) : (
                    ''
                  )}
                  <path
                    fill='#ffffff'
                    d='M0.132033381,1.5 C5.21400059,1.5 9.94842682,0.41025641 15.1320334,0 C15.2336727,0 15.7844924,2.26923077 15.377935,2.37179487 C13.6500662,2.78205128 11.9221973,3.08974359 10.0926891,3.29487179 C14.1320334,4.5 11.6320334,3.5 15.6320334,5 C18.6447537,6.12977013 16.1320334,5 19.6320334,7 C23.1320334,9 21.030394,8.98717949 21.1320334,9.5 C19.1320334,10 21.1320334,10 15.1320334,7.5 C12.3189879,6.32789772 12.8369514,6.16666667 4.70580387,4.62820513 C5.51891863,5.34615385 6.23039404,5.55128205 6.73859076,6.16666667 C8.26318092,7.91025641 9.07629568,7.70512821 10.2959678,9.75641026 C10.3976072,10.0641026 9.53039404,10.7948718 9.63203338,11 C7.49760715,8.74358974 5.82383666,8.21794872 3.58777109,6.16666667 C2.06318092,4.73076923 0.436951414,5.24358974 0.233672725,3.08974359 C0.132033381,2.78205128 -0.172884652,1.5 0.132033381,1.5 Z'
                    ref={node =>
                      d3
                        .select(node)
                        .attr(
                          'transform',
                          'translate(' +
                            (avgTransformX + avgTransformVars.pathX) +
                            ', ' +
                            (avgTransformY + avgTransformVars.pathY) +
                            ')rotate(' +
                            avgTransformVars.rotation +
                            ')' +
                            avgTransformVars.scale
                        )
                    }
                  />
                  <text
                    className='text-annotation'
                    x={avgTransformX + avgTransformVars.textX}
                    y={avgTransformY + avgTransformVars.textY}
                    textAnchor={avgTransformVars.textAnchor}
                    style={{ fontSize: '10px', fill: '#ffffff' }}
                  >
                    Durchschnittsleser
                  </text>
                  {userValue && userAge ? (
                    <circle
                      className='circle-you'
                      cx={FeatureGraph.xScale(userAge)}
                      cy={FeatureGraph.yScale(userValue)}
                      r={6}
                      ref={node => d3.select(node).attr('fill', '#54af71')}
                    />
                  ) : (
                    ''
                  )}
                  <path
                    fill='#ffffff'
                    d='M0.132033381,1.5 C5.21400059,1.5 9.94842682,0.41025641 15.1320334,0 C15.2336727,0 15.7844924,2.26923077 15.377935,2.37179487 C13.6500662,2.78205128 11.9221973,3.08974359 10.0926891,3.29487179 C14.1320334,4.5 11.6320334,3.5 15.6320334,5 C18.6447537,6.12977013 16.1320334,5 19.6320334,7 C23.1320334,9 21.030394,8.98717949 21.1320334,9.5 C19.1320334,10 21.1320334,10 15.1320334,7.5 C12.3189879,6.32789772 12.8369514,6.16666667 4.70580387,4.62820513 C5.51891863,5.34615385 6.23039404,5.55128205 6.73859076,6.16666667 C8.26318092,7.91025641 9.07629568,7.70512821 10.2959678,9.75641026 C10.3976072,10.0641026 9.53039404,10.7948718 9.63203338,11 C7.49760715,8.74358974 5.82383666,8.21794872 3.58777109,6.16666667 C2.06318092,4.73076923 0.436951414,5.24358974 0.233672725,3.08974359 C0.132033381,2.78205128 -0.172884652,1.5 0.132033381,1.5 Z'
                    ref={node =>
                      d3
                        .select(node)
                        .attr(
                          'transform',
                          'translate(' +
                            (userTransformX + userTransformVars.pathX) +
                            ', ' +
                            (userTransformY + userTransformVars.pathY) +
                            ')rotate(' +
                            userTransformVars.rotation +
                            ')' +
                            userTransformVars.scale
                        )
                    }
                  />
                  <text
                    className='text-annotation'
                    x={userTransformX + userTransformVars.textX}
                    y={userTransformY + userTransformVars.textY}
                    textAnchor={userTransformVars.textAnchor}
                    style={{ fontSize: '10px', fill: '#ffffff' }}
                  >
                    Sie
                  </text>
                </g>
              </g>
            </svg>
            {FeatureGraph.isMobile ? (
              <div className='tooltip' />
            ) : (
              <>
                <div className='tooltip tooltip-top desktop'>
                  <iframe
                    src={`https://open.spotify.com/embed/track/${this.props.data.songhigh}`}
                    width='300'
                    height='80'
                    frameBorder='0'
                    allowTransparency='true'
                    allow='encrypted-media'
                  />
                </div>
                <div className='tooltip tooltip-bottom desktop'>
                  <iframe
                    src={`https://open.spotify.com/embed/track/${this.props.data.songlow}`}
                    width='300'
                    height='80'
                    frameBorder='0'
                    allowTransparency='true'
                    allow='encrypted-media'
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </>
    )
  }
}

export default FeatureGraph
